import { type DependencyList, useMemo } from "react";
import { useServices } from "appServices";
import { addressDataToForm } from "helpers/forms/address";
import { getUseVirtualOffice, getVirtualOfficeBillingPeriod, getvirtualOfficeRegister } from "helpers/forms/company";
import { getBusinessActivitiesFormValues } from "helpers/forms/incorporation/businessActivity";
import { type AddressData } from "services/address/addressTypes";
import { type CompanyData, CompanyUpdateData } from "services/company/companyTypes";
import type { CompanyBusinessActivityData } from "services/companyBusinessActivities/gb/companyBusinessActivitiesTypes";
import { CompanyProduct, type VirtualOfficeProductDetailsData } from "services/companyProducts/companyProductsTypes";
import { IncorporationStatus } from "services/incorporation/incorporationTypes";
import { type ServicesGb } from "servicesGb";
import { type CompanyFormValues } from "./companyTypes";

export const formatCompanyName = (company?: CompanyData): string => {
  if (!company) {
    return "";
  }

  return `${company?.companyName} ${company?.namePostfix}`;
};

const companyDataToCompanyForm = (
  company?: CompanyData,
  address?: AddressData,
  businessActivities?: CompanyBusinessActivityData[],
  virtualOfficeProductDetails?: VirtualOfficeProductDetailsData,
  pricingPlanIncludesVirtualOffice?: boolean,
  hasSameDayRegistration?: boolean,
  hasPlusTaxes?: boolean,
  hasVatRegistration?: boolean,
  hasConfirmationStatement?: boolean,
  hasManagementSecretary: boolean = false,
  hasCancellationCover?: boolean,
  hasPayeRegistration?: boolean,
  ownsSharesNum?: number | undefined,
  mainDirectorId?: string | undefined,
): CompanyFormValues | undefined => {
  if (!company?.companyName || !company.namePostfix) {
    return;
  }

  return {
    name: company.companyName,
    namePostfix: company.namePostfix,
    country: company.country ?? undefined,
    natureOfBusiness: company.natureOfBusiness ?? undefined,
    startsTrading: company.startsTrading ?? undefined,
    address: addressDataToForm(address),
    businessActivities: getBusinessActivitiesFormValues(businessActivities),
    useVirtualOffice: getUseVirtualOffice(company, pricingPlanIncludesVirtualOffice),
    useHomeAddressAsRegistered: company.useHomeAddressAsRegistered ?? undefined,
    virtualOfficeRegister: getvirtualOfficeRegister(company),
    virtualOfficeBillingPeriod: getVirtualOfficeBillingPeriod(company, virtualOfficeProductDetails),
    sameDayRegistration: hasSameDayRegistration || undefined,
    annaTaxes: hasPlusTaxes || undefined,
    vatRegistration: hasVatRegistration || undefined,
    confirmationStatement: hasConfirmationStatement || undefined,
    managementSecretary: hasManagementSecretary,
    cancellationCover: hasCancellationCover,
    payeRegistration: hasPayeRegistration,
    directorCheck: !ownsSharesNum ? undefined : false,
    letterNoObjectionFileId: company.letterNoObjectionFileId ?? undefined,
    mainDirectorId: mainDirectorId ?? undefined,
    companyId: company.id,
  };
};

export const companyFormToCompanyUpdateData = (companyForm: CompanyFormValues): CompanyUpdateData => {
  return new CompanyUpdateData({
    companyName: companyForm.name,
    namePostfix: companyForm.namePostfix,
    natureOfBusiness: companyForm.natureOfBusiness,
    startsTrading: companyForm.startsTrading,
    country: companyForm.country,
    useVirtualOffice: companyForm.useVirtualOffice,
    useHomeAddressAsRegistered: companyForm.useHomeAddressAsRegistered,
  });
};

export const useCompanyFormInitialValue = (deps: DependencyList = []): CompanyFormValues | undefined => {
  const {
    companyStore,
    companyAddressStore,
    companyBusinessActivitiesStore,
    companyProductsStore,
    pricingPlanStore,
    directorStore,
    incorporationStore,
  } = useServices<ServicesGb>();

  const { directors } = directorStore;
  const ownsSharesNumRaw = directors ? directors[0]?.ownsSharesNum : undefined;
  const ownsSharesNum = ownsSharesNumRaw === null ? undefined : ownsSharesNumRaw;
  const isRejected = incorporationStore.incorporation?.status === IncorporationStatus.Rejected;
  const mainDirectorId = isRejected ? directorStore.getMainDirector()?.id : undefined;

  return useMemo(
    () =>
      companyDataToCompanyForm(
        companyStore.company,
        companyAddressStore.address,
        companyBusinessActivitiesStore?.activities,
        companyProductsStore.getProductDetails(CompanyProduct.VirtualOffice),
        pricingPlanStore.isProductIncluded(CompanyProduct.VirtualOffice),
        companyProductsStore.hasProduct(CompanyProduct.SameDayRegistration),
        companyProductsStore.hasProduct(CompanyProduct.Taxes),
        companyProductsStore.hasProduct(CompanyProduct.VatRegistration),
        companyProductsStore.hasProduct(CompanyProduct.ConfirmationStatement),
        companyProductsStore.hasProduct(CompanyProduct.DirectorManagement) || undefined,
        companyProductsStore.hasProduct(CompanyProduct.CancellationCover) || undefined,
        companyProductsStore.hasProduct(CompanyProduct.PayeRegistration) || undefined,
        ownsSharesNum,
        mainDirectorId,
      ),
    deps,
  );
};
